import { Component, Inject, OnInit, EventEmitter } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '@intranet/modules/users';
import {
    User,
    UserGroup,
    UserOrGroup,
} from '@intranet/modules/users/user.types';
import { switchMap } from 'rxjs-compat/operator/switchMap';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { statuses, priorities } from '../../action.utils';
import { ActionStatus, JobAction } from '../../action.types';
import { Priority } from '@intranet/core/types';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import { ClientsService } from '@intranet/modules/clients/clients.service';
import { Client } from '@intranet/modules/clients/client.types';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';
import { JobService } from '@intranet/modules/jobs/job.service';
import { Job, JobStatus } from '@intranet/modules/jobs/job.types';

export interface BulkDialogData {
        scheduleDate?: Date, 
        deadlineDate?: Date,
        clearDeadline?: boolean,
        actionStatus?: ActionStatus,
        waitingOnUserOrGroupId?: string | number,
        priority?: Priority,
        owners?: {userIds: string[];
            groupIds: number[];
        },
        jobId?: number,
        // The actions the bulk update applies to. Only ever populated when
        // opening the dialog (used to show the "current job(s)" info on the
        // Move tab) - never read back out of the closed dialog's result.
        actions?: JobAction[]
}

@Component({
    selector: 'action-bulk-dialog',
    templateUrl: './action-bulk-dialog.component.html',
    styleUrls: ['./action-bulk-dialog.component.scss']
})
export class ActionBulkDialog implements OnInit {

    scheduleDate: Date;
    deadlineDate: Date;
    clearDeadline?: boolean = false;
    users: User[];

    selectedStatus?: ActionStatus = null;
    selectedPriority?: Priority = null;
    waitingOnUserOrGroupId: string | number = null;
    selectedOwners: {userIds: string[], groupIds: number[]} = null;
    waitingUserIds: string[] = [];
    waitingGroupIds: number[] = [];

    // Move tab
    allClients: Client[] = [];
    clients: [Client[], Client[]] = [[], []];
    moveJobs: Job[] = [];
    currentJobs: { jobId: number; jobNumber: number; jobTitle: string; clientName: string }[] = [];
    selectedClientId: number | '' | null = null;
    selectedJobId: number | '' = '';

    get statuses() {
        return statuses;
    }

    get priorities() {
        return priorities;
    }

    protected userIds: string[] = [];
    protected groupIds: number[] = [];
    public ActionStatus = ActionStatus;

    updated: EventEmitter<{ scheduleDate?: Date, deadlineDate?: Date }>;

    constructor(public dialogRef:MatDialogRef<ActionBulkDialog>,
        @Inject(MAT_DIALOG_DATA) public data: BulkDialogData,
        private route: ActivatedRoute,
        private userService: UserService,
        private hotkeyService: HotkeyService,
        private _clientsService: ClientsService,
        private _jobService: JobService,
    ) { }
    ngOnInit(): void {
        // Register hotkey for "Cancel" action (Ctrl + Q)
        this.hotkeyService.registerHotkey('ctrl+q', 'Cancel action', () => this.onCancel());

        // Register hotkey for "Update" action (Ctrl + Alt + S)
        this.hotkeyService.registerHotkey('ctrl+alt+s', 'Update action', () => {
            if (this.dateValid && this.statusValid) {
              console.log('Update action');
                this.onSubmit();
            }
        });

        // Get users and groups
        this.users = this.userService.users.filter(
            (user) => user.active
        );
        //this.userGroups = this.userService.groups;

        if (typeof this.data.waitingOnUserOrGroupId === 'string') {
            this.waitingUserIds  = [ this.data.waitingOnUserOrGroupId ];
        } else if (typeof this.data.waitingOnUserOrGroupId === 'number') {
            this.waitingGroupIds = [ this.data.waitingOnUserOrGroupId ];
        }

        this.selectedStatus = this.data.actionStatus ?? null;

        this.waitingOnUserOrGroupId = this.data.waitingOnUserOrGroupId;

        this.currentJobs = this._buildCurrentJobs();

        // Load clients for the Move tab's client selector
        combineLatest([
            this._clientsService.clients$,
            this._clientsService.myClients$,
        ])
            .pipe(take(1))
            .subscribe(([allClients, myClients]) => {
                allClients = allClients ?? [];
                const [pinnedClients, restClients] = orderClientsByGroupName(
                    allClients.filter(c => !c.archive),
                    (myClients ?? []).map(c => c.clientId),
                    true
                );
                this.allClients = allClients;
                this.clients = [pinnedClients as Client[], restClients as Client[]];
            });
    }

    // Distinct jobs (job number, client name, title) that the selected
    // actions currently belong to - purely informational, shown on the
    // Move tab so the user can see what they're about to move. Built once
    // in ngOnInit rather than as a getter, so the list isn't rebuilt (and
    // re-rendered) on every change detection run.
    private _buildCurrentJobs(): { jobId: number; jobNumber: number; jobTitle: string; clientName: string }[] {
        const seen = new Map<number, { jobId: number; jobNumber: number; jobTitle: string; clientName: string }>();
        (this.data.actions ?? []).forEach(a => {
            if (a.jobId != null && !seen.has(a.jobId)) {
                seen.set(a.jobId, {
                    jobId: a.jobId,
                    jobNumber: a.jobNumber,
                    jobTitle: a.jobTitle,
                    clientName: a.clientName,
                });
            }
        });
        return Array.from(seen.values());
    }

    trackByJobId(_: number, job: { jobId: number }) {
        return job.jobId;
    }

    onMoveClientChange(clientId: number | '' | null) {
        this.selectedJobId = '';
        this.moveJobs = [];

        if (clientId === '' || clientId == null || isNaN(+clientId)) {
            return;
        }

        const id = Number(clientId);
        this._jobService
            .getJobs({ clientId: id, page: 1, status: JobStatus.Live, resultsPerPage: -1 })
            .subscribe(jobs => {
                this.moveJobs = jobs ?? [];
            });
    }

    onSubmit() {
        let result: BulkDialogData = {
            scheduleDate: this.scheduleDate,
            deadlineDate: this.deadlineDate,
            clearDeadline: this.clearDeadline,
            actionStatus: this.selectedStatus,
            waitingOnUserOrGroupId: this.waitingOnUserOrGroupId,
            priority: this.selectedPriority,
            owners: this.selectedOwners,
            jobId: this.selectedJobId ? Number(this.selectedJobId) : null
        };
        this.dialogRef.close(result);
    }

    onUserOrGroupSelected(event: { userIds: string[], groupIds: number[] }) {
        // pick first ID for your single‐value
        if (event.userIds.length) {
            this.waitingOnUserOrGroupId = event.userIds[0];
        } else if (event.groupIds.length) {
            this.waitingOnUserOrGroupId = event.groupIds[0];
        } else {
            this.waitingOnUserOrGroupId = null;
        }

        // mirror back into the arrays so the UI stays highlighted
        this.waitingUserIds  = [...event.userIds];
        this.waitingGroupIds = [...event.groupIds];

        // force “Waiting” status if they just picked someone
        if (event.userIds.length || event.groupIds.length) {
            this.selectedStatus = ActionStatus.Waiting;
        }
    }

    onStatus(event: any) {
    }

    onStatusChange(newStatus: ActionStatus) {
        // if the status is changed to Waiting, we need to ensure
        // that the waitingOnUserOrGroupId is set to something valid.
        if (newStatus !== ActionStatus.Waiting) {
            // wipe out any previous “waiting for” choice:
            this.waitingOnUserOrGroupId = null;
            this.waitingUserIds  = [];
            this.waitingGroupIds = [];
        }
    }

    onPriorityChange(event: any) {
    }

    onOwnerSelected(event: {userIds: string[], groupIds: number[]}) {
        this.selectedOwners = event;
    }

    onClearDate(field: string) {
        this[field] = null;
    }

    get dateValid() : boolean {
        if (this.scheduleDate && this.deadlineDate) {
            return this.scheduleDate <= this.deadlineDate;
        }
        return true;
    }

    get statusValid() : boolean {
        return this.selectedStatus === ActionStatus.Waiting ? this.waitingOnUserOrGroupId !== null : true;
    }


    onTest() {
   }

    onCancel() {
        this.dialogRef.close();
    }
}